/**
 * What a drop or a file pick turns into (W2-09 publish-import; DESIGN.md §5.12, §4.17; ARCHITECTURE.md §11):
 *
 *  - Planning (`planJobs`, pure): every .pez / .zip is one import; files from dropped or picked folders are grouped
 *    into chart folders (a folder holding a chart or a manifest; every file goes to its nearest such folder, so a whole
 *    RPE `Resources/` folder becomes one import per chart and textures in sub-folders stay with their chart); loose
 *    files are one import, except several loose charts without a manifest (difficulties of one song), which become
 *    one import per chart, each with the loose media. A folder without any chart is one media-only import (the
 *    importer answers "media only" and the new chart wizard starts).
 *  - Reading (`readJob`): an import's files as the io worker's `ImportInput` (paths relative to the chart folder).
 *  - Report helpers: entries grouped by class (D§4.17 order), counts, and the plain-text report for Copy / Save.
 */

import type { ImportClass, ImportReportEntry } from '../../core/ir/types';
import { kindOfLine } from '../../core/model/lineKind';
import type { ChartDoc, DocIndexes, LineId, TrackId } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import type { DetectedFormat, ImportInput, ImportReport } from '../../formats/types';
import { m } from './import.messages';

// ---------------------------------------------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------------------------------------------

/** Manifests Phira reads (in its precedence order). */
export const MANIFEST_NAMES: readonly string[] = ['info.yml', 'info.txt', 'info.csv'];
/** JSON files of a package that are never the chart. */
export const NOT_CHART_JSON: readonly string[] = ['extra.json', 'phiedit.json', 'export.json'];
const ARCHIVE_EXTS: readonly string[] = ['pez', 'zip'];
const CHART_EXTS: readonly string[] = ['json', 'pec'];

export type FileRole = 'archive' | 'chart' | 'manifest' | 'other';

function baseName(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/** What a file is to the planner, by its name. */
export function fileRole(name: string): FileRole {
  const base = baseName(name).toLowerCase();
  if (MANIFEST_NAMES.includes(base)) return 'manifest';
  const ext = extOf(base);
  if (ARCHIVE_EXTS.includes(ext)) return 'archive';
  if (CHART_EXTS.includes(ext) && !NOT_CHART_JSON.includes(base)) return 'chart';
  return 'other';
}

/** The file's path below a dropped or picked folder ('' for a loose file). */
export function relativePathOf(file: File): string {
  const rel = (file as Partial<Pick<File, 'webkitRelativePath'>>).webkitRelativePath;
  return typeof rel === 'string' ? rel.replace(/\\/g, '/').replace(/^\/+/, '') : '';
}

/** True when the files look like charts, packages or folders (the chart drop handler takes them first). */
export function looksLikeCharts(files: readonly File[]): boolean {
  return files.some((f) => relativePathOf(f).includes('/') || fileRole(f.name) !== 'other');
}

// ---------------------------------------------------------------------------------------------------------------
// Planning
// ---------------------------------------------------------------------------------------------------------------

export interface SourceFile {
  /** Path inside the import, '/'-separated, relative to its chart folder. */
  readonly path: string;
  readonly file: Blob;
}

export type JobSource =
  /** A .pez / .zip file. */
  | { readonly kind: 'archive'; readonly file: Blob }
  /** A chart folder or loose files. */
  | { readonly kind: 'files'; readonly files: readonly SourceFile[] }
  /** A package nested in a Phira batch export (`name`: its name inside the export). */
  | { readonly kind: 'bytes'; readonly name: string; readonly bytes: Uint8Array };

export interface ImportJob {
  readonly id: number;
  /** What the user recognises: the file name or the folder path. */
  readonly name: string;
  readonly source: JobSource;
}

export interface Plan {
  readonly jobs: readonly ImportJob[];
  /** Files left out (inside a dropped folder but not inside any chart folder). */
  readonly skipped: number;
}

function dirOf(path: string): string {
  const slash = path.lastIndexOf('/');
  return slash < 0 ? '' : path.slice(0, slash);
}

/** The nearest folder of `path` (itself included) in `roots`, or null. */
function nearestRoot(path: string, roots: ReadonlySet<string>): string | null {
  let dir = dirOf(path);
  for (;;) {
    if (roots.has(dir)) return dir;
    if (dir === '') return null;
    dir = dirOf(dir);
  }
}

/** Plans the imports of a drop or a pick (see the module header). `nextId` numbers the jobs. */
export function planJobs(files: readonly File[], nextId: () => number): Plan {
  const jobs: ImportJob[] = [];
  let skipped = 0;
  const foldered: Array<{ readonly path: string; readonly file: File }> = [];
  const loose: File[] = [];
  for (const f of files) {
    const rel = relativePathOf(f);
    if (fileRole(f.name) === 'archive') {
      jobs.push({ id: nextId(), name: rel !== '' ? rel : f.name, source: { kind: 'archive', file: f } });
    } else if (rel.includes('/')) {
      foldered.push({ path: rel, file: f });
    } else {
      loose.push(f);
    }
  }

  // Folders: one import per chart folder.
  if (foldered.length > 0) {
    const roots = new Set<string>();
    for (const f of foldered) {
      const role = fileRole(f.path);
      if (role === 'chart' || role === 'manifest') roots.add(dirOf(f.path));
    }
    if (roots.size === 0) {
      // No chart anywhere: each top-level folder is one media-only import.
      const byTop = new Map<string, SourceFile[]>();
      for (const f of foldered) {
        const top = f.path.slice(0, f.path.indexOf('/'));
        const list = byTop.get(top) ?? [];
        list.push({ path: f.path.slice(top.length + 1), file: f.file });
        byTop.set(top, list);
      }
      for (const [top, list] of byTop) jobs.push({ id: nextId(), name: top, source: { kind: 'files', files: list } });
    } else {
      const byRoot = new Map<string, SourceFile[]>();
      for (const root of [...roots].sort()) byRoot.set(root, []);
      for (const f of foldered) {
        const root = nearestRoot(f.path, roots);
        if (root === null) {
          skipped++;
          continue;
        }
        const path = root === '' ? f.path : f.path.slice(root.length + 1);
        byRoot.get(root)?.push({ path, file: f.file });
      }
      for (const [root, list] of byRoot) {
        if (list.length === 0) continue;
        jobs.push({
          id: nextId(),
          name: root === '' ? (list[0]?.path ?? '') : root,
          source: { kind: 'files', files: list },
        });
      }
    }
  }

  // Loose files.
  if (loose.length > 0) {
    const charts = loose.filter((f) => fileRole(f.name) === 'chart');
    const hasManifest = loose.some((f) => fileRole(f.name) === 'manifest');
    if (charts.length >= 2 && !hasManifest) {
      const media = loose.filter((f) => fileRole(f.name) !== 'chart');
      for (const chart of charts) {
        jobs.push({
          id: nextId(),
          name: chart.name,
          source: {
            kind: 'files',
            files: [chart, ...media].map((f) => ({ path: f.name, file: f })),
          },
        });
      }
    } else {
      const first = charts[0] ?? loose[0];
      jobs.push({
        id: nextId(),
        name: first?.name ?? '',
        source: { kind: 'files', files: loose.map((f) => ({ path: f.name, file: f })) },
      });
    }
  }
  return { jobs, skipped };
}

/** Reads a Blob's bytes (File.arrayBuffer where it exists). */
export async function readBytes(blob: Blob): Promise<Uint8Array> {
  const buf =
    typeof blob.arrayBuffer === 'function' ? await blob.arrayBuffer() : await new Response(blob).arrayBuffer();
  return new Uint8Array(buf);
}

/** The import's files as the io worker's input. */
export async function readJob(job: ImportJob): Promise<ImportInput> {
  const src = job.source;
  switch (src.kind) {
    case 'archive':
      return { kind: 'zip', name: baseName(job.name), bytes: await readBytes(src.file) };
    case 'bytes':
      return { kind: 'zip', name: baseName(src.name), bytes: src.bytes };
    case 'files': {
      const files: Array<{ path: string; bytes: Uint8Array }> = [];
      for (const f of src.files) files.push({ path: f.path, bytes: await readBytes(f.file) });
      return { kind: 'files', files };
    }
  }
}

/** Buffers to transfer to the worker with an input (each owned completely by the input). */
export function transferables(input: ImportInput): Transferable[] {
  const out: ArrayBuffer[] = [];
  const add = (b: Uint8Array): void => {
    if (b.byteOffset === 0 && b.byteLength === b.buffer.byteLength && b.buffer instanceof ArrayBuffer) {
      if (!out.includes(b.buffer)) out.push(b.buffer);
    }
  };
  if (input.kind === 'zip') add(input.bytes);
  else for (const f of input.files) add(f.bytes);
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Report helpers
// ---------------------------------------------------------------------------------------------------------------

/** Report classes in the order of the import report (D§4.17). */
export const CLASS_ORDER: readonly ImportClass[] = ['exact', 'normalized', 'changed', 'flagged', 'rpeOnly'];

export const CLASS_TITLES = {
  exact: m.classExact,
  normalized: m.classNormalized,
  changed: m.classChanged,
  flagged: m.classFlagged,
  rpeOnly: m.classRpeOnly,
} as const;

export function groupEntries(
  entries: readonly ImportReportEntry[],
): Readonly<Record<ImportClass, ImportReportEntry[]>> {
  const out: Record<ImportClass, ImportReportEntry[]> = {
    exact: [],
    normalized: [],
    changed: [],
    flagged: [],
    rpeOnly: [],
  };
  for (const e of entries) out[e.cls].push(e);
  return out;
}

/** Number of entries per class (occurrences folded into an entry count once each). */
export function classCounts(entries: readonly ImportReportEntry[]): Readonly<Record<ImportClass, number>> {
  const g = groupEntries(entries);
  return {
    exact: g.exact.length,
    normalized: g.normalized.length,
    changed: g.changed.length,
    flagged: g.flagged.length,
    rpeOnly: g.rpeOnly.length,
  };
}

export const FORMAT_TITLES: Readonly<Record<DetectedFormat, string>> = {
  rpe: m.formatRpe,
  'pgr-fv1': m.formatPgr1,
  'pgr-fv3': m.formatPgr3,
  pec: m.formatPec,
};

type Translate = {
  readonly t: (key: string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
};

/** "RPE · RPEVersion 150 · 31 lines · 1,204 notes · 4,880 events · manifest: info.yml". */
export function reportSummary(text: Translate, report: ImportReport): string {
  const parts = [text.t(FORMAT_TITLES[report.format])];
  if (report.rpeVersion !== undefined) parts.push(text.t(m.rpeVersionPart, { version: report.rpeVersion }));
  parts.push(text.t(m.linesPart, { count: report.counts.lines }));
  parts.push(text.t(m.notesPart, { count: report.counts.notes }));
  parts.push(text.t(m.eventsPart, { count: report.counts.events }));
  parts.push(text.t(m.manifestPart, { source: report.manifest }));
  return parts.join(' · ');
}

/** One report entry as a line of text: message, how often, where. */
export function entryText(text: Translate, e: ImportReportEntry): string {
  const extra: string[] = [];
  if (e.count !== undefined && e.count > 1) extra.push(text.t(m.entryTimes, { count: e.count }));
  if (e.line !== undefined) extra.push(text.t(m.entryLine, { line: e.line }));
  if (e.path !== undefined && e.path !== '') extra.push(e.path);
  return extra.length === 0 ? text.tr(e.message) : `${text.tr(e.message)} (${extra.join(', ')})`;
}

/** What an imported chart uses beyond plain lines and notes (the report's "Also read" line, UX-087). */
export interface DocFeatures {
  readonly effects: number;
  readonly customShaders: number;
  readonly videos: number;
  readonly texture: number;
  readonly gif: number;
  readonly text: number;
  readonly paint: number;
  readonly ui: number;
  readonly parents: number;
  readonly controls: number;
  readonly tempoChanges: number;
}

/** Counts what an imported chart uses: extra.json effects and videos, special lines, UI lines, parents, controls. */
export function docFeatures(doc: ChartDoc): DocFeatures {
  const props = new Map<LineId, Map<string, TrackId[]>>();
  for (const t of doc.tracks.values()) {
    if (t.owner.kind !== 'line') continue;
    let byProp = props.get(t.owner.line);
    if (byProp === undefined) props.set(t.owner.line, (byProp = new Map<string, TrackId[]>()));
    byProp.set(t.owner.prop, [...(byProp.get(t.owner.prop) ?? []), t.id]);
  }
  const idx = {
    tracksOfLine: (id: LineId) =>
      (props.get(id) ?? new Map<string, TrackId[]>()) as ReturnType<DocIndexes['tracksOfLine']>,
  };
  const out = { texture: 0, gif: 0, text: 0, paint: 0, ui: 0, parents: 0, controls: 0 };
  for (const line of doc.lines.values()) {
    const kind = kindOfLine(doc, idx, line);
    if (kind !== 'normal') out[kind]++;
    if (line.attachUI !== null) out.ui++;
    if (line.parent !== null) out.parents++;
    const c = line.controls;
    if (c.pos !== null || c.alpha !== null || c.size !== null || c.y !== null) out.controls++;
  }
  let customShaders = 0;
  for (const e of doc.effects.values()) if (e.shader.kind !== 'builtin') customShaders++;
  return {
    ...out,
    effects: doc.effects.size,
    customShaders,
    videos: doc.videos.size,
    tempoChanges: Math.max(0, doc.bpm.size - 1),
  };
}

/** "extra.json: 3 effects (1 custom shader), 2 image lines, 1 GIF line, ..." or null when there is nothing special. */
export function featureLine(text: Translate, doc: ChartDoc): string | null {
  const f = docFeatures(doc);
  const parts: string[] = [];
  if (f.effects > 0) parts.push(text.t(m.featEffects, { count: f.effects, custom: f.customShaders }));
  if (f.videos > 0) parts.push(text.t(m.featVideos, { count: f.videos }));
  if (f.tempoChanges > 0) parts.push(text.t(m.featTempo, { count: f.tempoChanges }));
  if (f.texture > 0) parts.push(text.t(m.featTexture, { count: f.texture }));
  if (f.gif > 0) parts.push(text.t(m.featGif, { count: f.gif }));
  if (f.text > 0) parts.push(text.t(m.featText, { count: f.text }));
  if (f.paint > 0) parts.push(text.t(m.featPaint, { count: f.paint }));
  if (f.ui > 0) parts.push(text.t(m.featUi, { count: f.ui }));
  if (f.parents > 0) parts.push(text.t(m.featParents, { count: f.parents }));
  if (f.controls > 0) parts.push(text.t(m.featControls, { count: f.controls }));
  if (parts.length === 0) return null;
  return text.t(m.alsoRead, { list: parts.join(' · ') });
}

/** Missing files of an import (placeholders in the document). */
export function missingCount(doc: { readonly assets: ReadonlyMap<unknown, { readonly missing?: boolean }> }): number {
  let n = 0;
  for (const a of doc.assets.values()) if (a.missing === true) n++;
  return n;
}

/** The full report as plain text (Copy report / Save report). */
export function reportText(text: Translate, report: ImportReport, missing: number): string {
  const lines: string[] = [];
  lines.push(text.t(m.reportTextTitle, { name: report.fileName }));
  lines.push(reportSummary(text, report));
  lines.push(text.t(report.phiraWouldLoad ? m.phiraLoads : m.phiraRefuses));
  lines.push(
    text.t(m.exactSummary, {
      events: report.counts.events,
      notes: report.counts.notes,
      files: report.counts.assets,
      missing,
    }),
  );
  const groups = groupEntries(report.entries);
  for (const cls of CLASS_ORDER) {
    const list = groups[cls];
    if (list.length === 0) continue;
    lines.push('');
    lines.push(`${text.t(CLASS_TITLES[cls])} (${String(list.length)})`);
    for (const e of list) lines.push(`  - ${entryText(text, e)}`);
  }
  if (report.phiraImportWarnings.length > 0) {
    lines.push('');
    lines.push(text.t(m.phiraWarnings));
    for (const w of report.phiraImportWarnings) lines.push(`  - ${text.tr(w)}`);
  }
  return lines.join('\n');
}

/** A file name for a saved report ("Song.pez import report.txt"). */
export function reportFileName(text: Translate, name: string): string {
  const clean = baseName(name)
    .replace(/[\\/:*?"<>|]/g, '_')
    .trim();
  return text.t(m.reportFileName, { name: clean === '' ? 'chart' : clean });
}

/** Offers text as a download. */
export function downloadText(content: string, fileName: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
}

/** Writes text to the system clipboard; false when the browser refuses. */
export async function copyText(content: string): Promise<boolean> {
  try {
    const clip = (navigator as Partial<Navigator>).clipboard;
    if (clip === undefined) return false;
    await clip.writeText(content);
    return true;
  } catch {
    return false;
  }
}

/** Opens the browser's file picker (files, or a folder with `directory`); resolves with the chosen files. */
export function pickFiles(opts: { readonly directory: boolean; readonly accept?: string }): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    if (opts.accept !== undefined && !opts.directory) input.accept = opts.accept;
    if (opts.directory) {
      input.setAttribute('webkitdirectory', '');
      input.setAttribute('directory', '');
    }
    input.style.display = 'none';
    const done = (files: File[]): void => {
      input.remove();
      resolve(files);
    };
    input.addEventListener(
      'change',
      () => {
        done(input.files === null ? [] : [...input.files]);
      },
      { once: true },
    );
    input.addEventListener(
      'cancel',
      () => {
        done([]);
      },
      { once: true },
    );
    document.body.append(input);
    input.click();
  });
}

/** What the "Open a chart file" picker offers. */
export const PICK_ACCEPT =
  '.pez,.zip,.json,.pec,.yml,.txt,.csv,audio/*,image/*,video/*,.ogg,.mp3,.wav,.flac,.png,.jpg,.jpeg,.webp,.gif,.glsl';
